import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import WordDetailSheet from "@/components/vocabulary/WordDetailSheet";
import AddWordManualDialog from "@/components/vocabulary/AddWordManualDialog";
import EditWordDialog from "@/components/vocabulary/EditWordDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Star, Search as SearchIcon, Layers, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { value: "all", label: "Все" },
  { value: "new", label: "Новые" },
  { value: "today", label: "Сегодня" },
  { value: "review", label: "На повторение" },
  { value: "learned", label: "Изучены" },
  { value: "favorite", label: "Избранные" },
];

const STATUS_STYLE = {
  learning: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300",
  review: "bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300",
  mastered: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300",
};

export default function Vocabulary() {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedWord, setSelectedWord] = useState(null);
  const [editWord, setEditWord] = useState(null);
  const [addManualOpen, setAddManualOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const queryClient = useQueryClient();

  const { data: words = [], isLoading } = useQuery({
    queryKey: ["words", filter, search],
    queryFn: async () => (await api.get("/words", { params: { filter, search } })).data,
  });

  const favoriteMutation = useMutation({
    mutationFn: ({ id, favorite }) => api.patch(`/words/${id}`, { favorite }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["words"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/words/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["words"] });
      setSelectedWord(null);
      toast.success("Слово удалено");
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids) => api.post("/words/bulk-delete", { ids }),
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries({ queryKey: ["words"] });
      toast.success(`Удалено слов: ${ids.length}`);
      setSelectedIds([]);
    },
    onError: () => toast.error("Не удалось удалить выбранные слова"),
  });

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => (prev.length === words.length ? [] : words.map((w) => w.id)));
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="vocabulary-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Словарь</h1>
        <p className="text-sm text-muted-foreground mt-1">Все изученные слова с их контекстами</p>
      </div>

      <div className="flex justify-end">
        <Button onClick={() => setAddManualOpen(true)} data-testid="add-word-manual-button">
          <Plus className="w-4 h-4 mr-1" /> Добавить слово вручную
        </Button>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="flex-wrap h-auto" data-testid="vocabulary-filter-tabs">
            {FILTERS.map((f) => (
              <TabsTrigger key={f.value} value={f.value} data-testid={`vocabulary-filter-${f.value}`}>
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="relative w-full sm:w-64">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Поиск слов..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="vocabulary-search-input" />
        </div>
      </div>

      {words.length > 0 && (
        <div className="flex items-center gap-3 px-1" data-testid="vocabulary-bulk-toolbar">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <Checkbox
              checked={selectedIds.length === words.length}
              onCheckedChange={toggleSelectAll}
              data-testid="vocabulary-select-all-checkbox"
            />
            Выбрать все
          </label>
          {selectedIds.length > 0 && (
            <>
              <span className="text-sm text-muted-foreground">Выбрано: {selectedIds.length}</span>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" data-testid="vocabulary-bulk-delete-button">
                    <Trash2 className="w-4 h-4 mr-1" /> Удалить выбранные
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="vocabulary-bulk-delete-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Удалить {selectedIds.length} слов(а)?</AlertDialogTitle>
                    <AlertDialogDescription>Это действие нельзя отменить.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="vocabulary-bulk-delete-cancel">Отмена</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => bulkDeleteMutation.mutate(selectedIds)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      data-testid="vocabulary-bulk-delete-confirm"
                    >
                      Удалить
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-16 text-muted-foreground">Загрузка...</div>
      ) : words.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center gap-3" data-testid="vocabulary-empty-state">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
            <Layers className="w-7 h-7 text-muted-foreground" />
          </div>
          <p className="font-medium">Слов пока нет</p>
          <p className="text-sm text-muted-foreground max-w-xs">Нажимайте на незнакомые слова в субтитрах видео, чтобы добавить их сюда.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden" data-testid="vocabulary-list">
          {words.map((w) => (
            <div
              key={w.id}
              onClick={() => setSelectedWord(w)}
              data-testid={`vocabulary-row-${w.id}`}
              className="flex items-center gap-4 px-4 py-3 border-b last:border-b-0 border-border bg-card hover:bg-accent/50 cursor-pointer transition-colors duration-150"
            >
              <Checkbox
                checked={selectedIds.includes(w.id)}
                onCheckedChange={() => toggleSelect(w.id)}
                onClick={(e) => e.stopPropagation()}
                data-testid={`vocabulary-select-${w.id}`}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  favoriteMutation.mutate({ id: w.id, favorite: !w.favorite });
                }}
                data-testid={`vocabulary-favorite-${w.id}`}
              >
                <Star className={`w-4 h-4 ${w.favorite ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"}`} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{w.word}</span>
                  <span className="text-xs text-muted-foreground">{w.transcription}</span>
                </div>
                <p className="text-sm text-muted-foreground truncate">{w.translation}</p>
              </div>
              <span className="text-xs text-muted-foreground hidden sm:inline">{w.contexts?.length || 0} контекст(ов)</span>
              <Badge className={`border-0 ${STATUS_STYLE[w.status] || STATUS_STYLE.learning}`}>{w.status}</Badge>
            </div>
          ))}
        </div>
      )}

      <WordDetailSheet
        word={selectedWord}
        open={!!selectedWord}
        onOpenChange={(o) => !o && setSelectedWord(null)}
        onToggleFavorite={(w) => favoriteMutation.mutate({ id: w.id, favorite: !w.favorite })}
        onDelete={(w) => deleteMutation.mutate(w.id)}
        onEdit={(w) => setEditWord(w)}
      />
      <EditWordDialog
        word={editWord}
        open={!!editWord}
        onOpenChange={(o) => !o && setEditWord(null)}
        onSaved={(updated) => setSelectedWord(updated)}
      />
      <AddWordManualDialog open={addManualOpen} onOpenChange={setAddManualOpen} />
    </div>
  );
}
